import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  MOBILE_BREAKPOINT,
  PillSwitchButtons,
  Skeleton,
  Tooltip,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { InfoIconPopover } from 'components/atoms/InfoIconPopover';
import { ON_INFO_SURFACE } from 'components/atoms/InfoPopover';
import { getIconForRankValue, RANK_MAP } from 'components/atoms/RankIcons';
import {
  PerformanceWidgetMultiSelect,
  PerformanceWidgetSelect,
  PerformanceWidgetSelectPortalStyles,
} from 'components/atoms/PerformanceWidgetSelect';
import { DashboardWidgetContainer } from 'components/atoms/DashboardWidgetContainer';
import { LeaderboardDateRangeModal } from 'components/molecules/LeaderboardDateRangeModal';
import { PowerScoreLegend } from 'components/molecules/PowerScoreLegend';
import { StatusLegend } from 'components/molecules/StatusLegend';
import {
  getNewDashboardOrgType,
  type NewDashboardOrgType,
} from 'helpers/repTypeHelper';
import { formatDateRangeMMDDYYYY } from 'helpers/dateHelpers';
import {
  getLeaderboardQuartile,
  getLeaderboardRowBackgroundColor,
  getLeaderboardRowHighlightBorderColor,
  type LeaderboardQuartile,
} from 'helpers/leaderboardQuartiles';
import {
  filterEntriesByTitle,
  getUniqueTitles,
  reconcileTitleSelection,
  titleSelectionFromCheckedIds,
  type TitleSelection,
} from 'helpers/leaderboardTitles';
import {
  DEFAULT_VIP_VIEW,
  isVipViewer,
  VIP_IDENTITIES,
  VIP_VIEW_OPTIONS,
  type VipView,
} from 'helpers/vipLeaderboard';
import { METRIC_INFO_BODIES } from 'constants/metricInfoBodies';
import { useUserProfile } from 'providers/UserProfile';
import {
  useLeaderboardData,
  useRepProfileData,
  type LeaderboardDateRange,
} from 'hooks/usePerformanceData';
import type {
  LeaderboardAggregatedEntry,
  LeaderboardAggregatedMetrics,
  LeaderboardData,
  LeaderboardRangeKey,
  LeaderboardRepEntry,
  LeaderboardRepMetrics,
  LeaderboardViewScopeKey,
} from 'types/performance';

type ViewScope = LeaderboardViewScopeKey;
type GroupScope = 'street' | 'region' | 'teams' | 'squads';
type MetricMode = 'actual' | 'pacing';
type LeaderboardEntry = LeaderboardAggregatedEntry | LeaderboardRepEntry;
type AggregatedMetricKey = keyof LeaderboardAggregatedMetrics;
type RepMetricKey = keyof LeaderboardRepMetrics;
type SortDirection = 'asc' | 'desc';
type SortColumn = 'name' | AggregatedMetricKey | RepMetricKey | 'status';

const STREET_VIEW_SCOPE_OPTIONS: { label: string; value: ViewScope }[] = [
  { label: 'Rep', value: 'rep' },
  { label: 'Squad', value: 'squad' },
  { label: 'Office', value: 'office' },
  { label: 'Region', value: 'region' },
  { label: 'VP', value: 'vp' },
];

const CHAIN_VIEW_SCOPE_OPTIONS: { label: string; value: ViewScope }[] = [
  { label: 'Rep', value: 'rep' },
  { label: 'Team', value: 'team' },
  { label: 'Metro Market', value: 'metro_market' },
  { label: 'Region', value: 'region' },
  { label: 'VP', value: 'vp' },
];

const getViewScopeOptions = (orgType: NewDashboardOrgType) => {
  switch (orgType) {
    case 'CHAIN':
    case 'AIR':
      return CHAIN_VIEW_SCOPE_OPTIONS;
    case 'BLOCK':
    case 'STREET':
    case 'UNKNOWN':
      return STREET_VIEW_SCOPE_OPTIONS;
    default: {
      const exhaustiveCheck: never = orgType;
      return exhaustiveCheck;
    }
  }
};

const getAllOrgLabel = (orgType: NewDashboardOrgType): string => {
  switch (orgType) {
    case 'CHAIN':
      return 'All Chain';
    case 'AIR':
      return 'All Air';
    case 'BLOCK':
      return 'All Block';
    case 'STREET':
    case 'UNKNOWN':
      return 'All Street';
    default: {
      const exhaustiveCheck: never = orgType;
      return exhaustiveCheck;
    }
  }
};

const getGroupScopeBaseOptions = (
  orgType: NewDashboardOrgType,
): { label: string; value: GroupScope }[] => [
  { label: getAllOrgLabel(orgType), value: 'street' },
  { label: 'My Region', value: 'region' },
  { label: 'My Team', value: 'teams' },
  { label: 'My Squad', value: 'squads' },
];

const hasAllOrgWideAccess = (data: LeaderboardData) =>
  !data.user_region ||
  (data.region ?? []).some(
    (entry) => entry.region && entry.region !== data.user_region,
  );

type GroupScopeOption = {
  label: string;
  value: GroupScope;
  disabled?: boolean;
};

const getGroupScopeOptions = (
  data: LeaderboardData | null,
  orgType: NewDashboardOrgType,
): GroupScopeOption[] =>
  getGroupScopeBaseOptions(orgType).flatMap((option) => {
    if (option.value === 'street') {
      return data && hasAllOrgWideAccess(data) ? [option] : [];
    }

    if (option.value === 'region') {
      return [{ ...option, disabled: !data?.user_region }];
    }

    if (option.value === 'teams') {
      return [{ ...option, disabled: !data?.user_team }];
    }

    return [{ ...option, disabled: !data?.user_squad }];
  });

const isGroupScopeEnabled = (
  options: GroupScopeOption[],
  scope: GroupScope,
): boolean => {
  const option = options.find(({ value }) => value === scope);
  return !!option && !option.disabled;
};

const getDefaultGroupScope = (options: GroupScopeOption[]): GroupScope => {
  const streetOption = options.find(({ value }) => value === 'street');
  if (streetOption && !streetOption.disabled) {
    return 'street';
  }

  const firstEnabled = options.find(({ disabled }) => !disabled);
  return firstEnabled?.value ?? 'street';
};

const isGroupScopeToggleDisabled = (options: GroupScopeOption[]) => {
  const regionOption = options.find(({ value }) => value === 'region');
  const teamsOption = options.find(({ value }) => value === 'teams');
  const squadsOption = options.find(({ value }) => value === 'squads');

  return (
    !!regionOption?.disabled &&
    !!teamsOption?.disabled &&
    !!squadsOption?.disabled
  );
};

const getAvailableViewScopes = (
  data: LeaderboardData | null,
  orgType: NewDashboardOrgType,
) =>
  getViewScopeOptions(orgType).filter(
    ({ value }) => (data?.[value]?.length ?? 0) > 0,
  );

const getDefaultViewScope = (
  data: LeaderboardData,
  orgType: NewDashboardOrgType,
): ViewScope => {
  const available = getAvailableViewScopes(data, orgType);
  const preferred = available.find(({ value }) => value === 'rep');
  return (preferred ?? available[0]).value;
};

const isPacingUnavailable = (rangeKey: LeaderboardRangeKey) =>
  rangeKey === 'today' || rangeKey === 'yesterday' || rangeKey === 'custom';

const DEFAULT_RANGE_KEY: LeaderboardRangeKey = 'wtd';

const TIMEFRAME_OPTIONS: { label: string; value: LeaderboardRangeKey }[] = [
  { label: 'Quarter', value: 'qtr' },
  { label: 'Month', value: 'mtd' },
  { label: 'Week', value: 'wtd' },
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last Week', value: 'last_week' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Last Quarter', value: 'last_quarter' },
  { label: 'Custom date', value: 'custom' },
];

const METRIC_MODE_OPTIONS: { label: string; value: MetricMode }[] = [
  { label: 'Actual', value: 'actual' },
  { label: 'Pacing', value: 'pacing' },
];

const AGGREGATED_METRIC_KEYS: AggregatedMetricKey[] = [
  'CS',
  'SRA',
  'CAP',
  'IC',
  'X',
];
const REP_METRIC_KEYS: RepMetricKey[] = ['CS', 'SRA', 'CAP', 'IC', 'X'];
const BLOCK_REP_METRIC_KEYS: RepMetricKey[] = [
  'LG',
  'SIT',
  'CS',
  'SRA',
  'CAP',
  'IC',
  'X',
];
type ChainMetricKey = 'OPPTY' | 'SIT' | 'SRA' | 'CS' | 'CAP' | 'IC' | 'SRA_SGR';
const CHAIN_METRIC_KEYS: ChainMetricKey[] = [
  'OPPTY',
  'SIT',
  'CS',
  'SRA',
  'SRA_SGR',
  'CAP',
  'IC',
];

const METRIC_LABELS: Record<AggregatedMetricKey | RepMetricKey, string> = {
  CS: 'Signatures',
  SRA: 'Sales',
  CAP: 'Close',
  IC: 'Installs',
  LG: 'Leads',
  SIT: 'Sits',
  OPPTY: 'Opportunity',
  SRA_SGR: 'SGR Sales',
  X: 'Power score',
};

const POWER_SCORE_METRIC_KEY = 'X';
const DEFAULT_SORT_COLUMN: SortColumn = POWER_SCORE_METRIC_KEY;

const INACTIVE_HEADER_COLOR = '#49517d';

type PerformanceTier = 'green' | 'gold' | 'red';

const TIER_COLORS: Record<PerformanceTier, string> = {
  green: '#1A8764',
  gold: '#A0781A',
  red: '#D43F4B',
};

const paceTierFromPace = (pace: number): PerformanceTier => {
  if (pace >= 1) {
    return 'green';
  }
  if (pace >= 0.9) {
    return 'gold';
  }
  return 'red';
};

const getMetricPaceTier = (
  entry: LeaderboardEntry,
  metricKey: AggregatedMetricKey,
  rangeKey: LeaderboardRangeKey,
): PerformanceTier | undefined => {
  if (isRepEntry(entry) || metricKey === 'X') {
    return undefined;
  }

  const rangeMetrics = entry.metrics_by_range[rangeKey];
  const metric = rangeMetrics?.[metricKey];
  if (!metric || typeof metric === 'number' || metric.pace == null) {
    return undefined;
  }

  const pace = metric.pace;
  if (pace < 0) {
    return undefined;
  }

  return paceTierFromPace(pace);
};

const formatRank = (rank: number): string =>
  `${String(rank).padStart(2, '0')}.`;

type TableGridColumnsOptions = {
  isRepOrSquadView: boolean;
  isBlockUser: boolean;
  isChainUser?: boolean;
  isMobile?: boolean;
  hasStatusColumn: boolean;
};

const getTableGridTemplateColumns = ({
  isRepOrSquadView,
  isBlockUser,
  isChainUser = false,
  isMobile = false,
  hasStatusColumn,
}: TableGridColumnsOptions): string => {
  const nameWidth = isMobile ? '120px' : '175px';

  if (isChainUser) {
    const metrics = `repeat(${CHAIN_METRIC_KEYS.length}, ${
      isMobile ? '65px' : 'minmax(65px, 1fr)'
    })`;
    if (hasStatusColumn) {
      return `${nameWidth} ${metrics} minmax(108px, 1fr)`;
    }
    return `${nameWidth} ${metrics}`;
  }

  if (isRepOrSquadView) {
    return `${nameWidth} repeat(${isBlockUser ? 6 : 4}, ${
      isMobile ? '65px' : '1fr'
    }) repeat(${hasStatusColumn ? 2 : 1}, minmax(108px, 1fr))`;
  }
  return `${nameWidth} repeat(4, minmax(65px, 1fr)) ${
    isMobile ? '2fr' : '1fr'
  }`;
};

type LeaderboardWidgetProps = {
  onTableScroll?: (scrollTop: number) => void;
};

const ChevronDownIcon = () => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="none"
    aria-hidden="true"
    focusable="false">
    <path
      d="M1 1L5 5L9 1"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const isRepEntry = (entry: LeaderboardEntry): entry is LeaderboardRepEntry =>
  'status' in entry && typeof entry.status === 'string';

const formatLeaderboardTeamName = (
  name: string,
  isBlockUser: boolean,
  applyFormatting: boolean,
): string => {
  if (!applyFormatting) {
    return name;
  }

  const prefixLength = isBlockUser
    ? 9 // Remove "FUSION-01" prefix
    : 5; // Remove "TX-01" prefix

  return name.length > prefixLength ? name.slice(prefixLength).trim() : name;
};

const isCurrentUserRow = (
  entry: LeaderboardEntry,
  viewScope: ViewScope,
  userTeam: string | undefined,
  employeeId: string | undefined,
  userRegion: string | undefined,
): boolean => {
  if (viewScope === 'rep' && !!employeeId) {
    return entry.entity_id === employeeId;
  }

  if (viewScope === 'region' && !!userRegion) {
    return entry.region === userRegion;
  }

  if (viewScope === 'office' && !!userTeam) {
    return (
      entry.entity_id === userTeam ||
      entry.name === userTeam ||
      entry.office === userTeam
    );
  }

  return false;
};

const resolveLeaderboardEntries = (
  data: LeaderboardData,
  viewScope: ViewScope,
  groupScope: GroupScope,
): LeaderboardEntry[] => {
  const baseEntries: LeaderboardEntry[] = data[viewScope] ?? [];

  switch (groupScope) {
    case 'region':
      return baseEntries.filter((entry) => entry.region === data.user_region);
    case 'teams':
      return baseEntries.filter(
        (entry) =>
          entry.office === data.user_team || entry.team === data.user_team,
      );
    case 'squads':
      return baseEntries.filter((entry) => {
        if (!data.user_squad) {
          return false;
        }
        if (viewScope === 'squad') {
          return entry.name === data.user_squad;
        }
        return isRepEntry(entry) && entry.squad === data.user_squad;
      });
    default: {
      return baseEntries;
    }
  }
};

const getMetricValue = (
  entry: LeaderboardEntry,
  metricKey: AggregatedMetricKey | RepMetricKey,
  metricMode: MetricMode,
  rangeKey: LeaderboardRangeKey,
): number => {
  if (isRepEntry(entry)) {
    const repRange = entry.metrics_by_range[rangeKey];
    if (!repRange) {
      return 0;
    }
    return repRange[metricKey as RepMetricKey] ?? 0;
  }

  const aggregatedRange = entry.metrics_by_range[rangeKey];
  if (!aggregatedRange) {
    return 0;
  }

  const aggregatedMetric = aggregatedRange[metricKey as AggregatedMetricKey];
  if (aggregatedMetric == null) {
    return 0;
  }
  if (typeof aggregatedMetric === 'number') {
    return aggregatedMetric;
  }

  return metricMode === 'pacing'
    ? aggregatedMetric.pace
    : Number(aggregatedMetric.value);
};

const getRankFromStatusName = (status: string): number => {
  const normalized = status.toUpperCase();
  if (normalized in RANK_MAP) {
    return RANK_MAP[normalized as keyof typeof RANK_MAP];
  }
  return 0;
};

const STATUS_SHORT_LABELS: Record<number, string> = {
  5: 'F',
  4: 'Pr',
  3: 'L3',
  2: 'L2',
  1: 'L1',
  0: 'U',
};

const getStatusShortLabel = (
  rank: number,
  orgType?: NewDashboardOrgType,
): string => {
  // CHAIN/BLOCK use Level 4 instead of Premier at rank 4
  if (rank === 4 && (orgType === 'CHAIN' || orgType === 'BLOCK')) {
    return 'L4';
  }
  return STATUS_SHORT_LABELS[rank] ?? 'U';
};

const getSortValue = (
  entry: LeaderboardEntry,
  column: SortColumn,
  metricMode: MetricMode,
  rangeKey: LeaderboardRangeKey,
  isBlockUser: boolean,
  viewScope: ViewScope,
): string | number => {
  if (column === 'name') {
    return formatLeaderboardTeamName(
      entry.name,
      isBlockUser,
      viewScope === 'office',
    ).toLowerCase();
  }

  if (column === 'status') {
    return isRepEntry(entry) ? getRankFromStatusName(entry.status) : 0;
  }

  return getMetricValue(entry, column, metricMode, rangeKey);
};

const getAriaSortValue = (
  isActive: boolean,
  sortDirection: SortDirection,
): 'none' | 'ascending' | 'descending' => {
  if (!isActive) {
    return 'none';
  }
  return sortDirection === 'asc' ? 'ascending' : 'descending';
};

const formatMetricValue = (
  value: number,
  metricMode: MetricMode,
  metricKey: AggregatedMetricKey | RepMetricKey,
): string => {
  if (metricMode === 'pacing' && metricKey !== 'X') {
    return value >= 0 ? `${(value * 100).toFixed(1)}%` : '-';
  }
  return value.toLocaleString();
};

const LeaderboardWidget = ({ onTableScroll }: LeaderboardWidgetProps) => {
  const { userProfile } = useUserProfile();
  const [searchParams] = useSearchParams();
  const [vipView, setVipView] = useState<VipView>(DEFAULT_VIP_VIEW);

  const isVip = isVipViewer(
    searchParams.get('email') ?? userProfile?.email ?? null,
  );
  const vipIdentity = isVip ? VIP_IDENTITIES[vipView] : undefined;

  // A VIP viewing the Block org is a Street rep, so the column layout and the
  // RunX status legend follow the impersonated identity rather than the
  // signed-in profile.
  const orgType = vipIdentity
    ? vipIdentity.orgType
    : getNewDashboardOrgType(userProfile);
  const isBlockUser = orgType === 'BLOCK';
  const isChainUser = orgType === 'CHAIN';

  const [viewScope, setViewScope] = useState<ViewScope>('rep');
  const [groupScope, setGroupScope] = useState<GroupScope>('street');
  const [metricMode, setMetricMode] = useState<MetricMode>('actual');
  const [titleSelection, setTitleSelection] = useState<TitleSelection>('all');
  const [rangeKey, setRangeKey] =
    useState<LeaderboardRangeKey>(DEFAULT_RANGE_KEY);
  const [customRange, setCustomRange] = useState<LeaderboardDateRange | null>(
    null,
  );
  const [isCustomRangeModalOpen, setIsCustomRangeModalOpen] = useState(false);
  const [rangeBeforeCustom, setRangeBeforeCustom] =
    useState<LeaderboardRangeKey>(DEFAULT_RANGE_KEY);
  const [sortColumn, setSortColumn] = useState<SortColumn>(DEFAULT_SORT_COLUMN);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
  const [isHorizontallyScrolled, setIsHorizontallyScrolled] = useState(false);

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      onTableScroll?.(target.scrollTop);
      setIsHorizontallyScrolled(target.scrollLeft > 0);
    },
    [onTableScroll],
  );

  const sellsSunrun = userProfile?.sellsSunrun === 'Yes';
  const isMobile = useMediaQuery({ maxWidth: MOBILE_BREAKPOINT });

  const renderControlSelect = <T extends string>(
    options: { label: string; value: T }[],
    value: T | '',
    onChange: (nextValue: T) => void,
    disabled?: boolean,
    dropdownInPortal = true,
    minWidth?: string,
    placeholder?: string,
    menuAlign?: 'left' | 'right',
  ) => (
    <ControlSelect
      $minWidth={minWidth}
      options={options}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      dropdownInPortal={dropdownInPortal}
      menuAlign={menuAlign}
      menuMaxHeight="available"
      onChange={(event) => onChange(event.target.value as T)}
      selectType="button"
      buttonProps={{
        color: 'tertiary',
        size: 'sm',
      }}
    />
  );

  const appliedRange = rangeKey === 'custom' ? customRange : null;
  const customRangeLabel =
    rangeKey === 'custom' && customRange
      ? formatDateRangeMMDDYYYY(customRange.startDate, customRange.endDate)
      : undefined;
  const dataRangeKey =
    rangeKey === 'custom' && !customRange ? rangeBeforeCustom : rangeKey;

  const { leaderboardData, isLeaderboardDataLoading, leaderboardDataError } =
    useLeaderboardData(
      vipIdentity && {
        email: vipIdentity.email,
        employeeId: vipIdentity.employeeId,
      },
      appliedRange ?? undefined,
    );
  const { profileData } = useRepProfileData();

  const availableViewScopes = useMemo(() => {
    const scopes = getAvailableViewScopes(leaderboardData, orgType);
    return scopes.length > 0 ? scopes : [getViewScopeOptions(orgType)[0]];
  }, [leaderboardData, orgType]);

  const groupScopeOptions = useMemo(
    () => getGroupScopeOptions(leaderboardData, orgType),
    [leaderboardData, orgType],
  );

  const isGroupScopeSelectionLocked = useMemo(
    () => isGroupScopeToggleDisabled(groupScopeOptions),
    [groupScopeOptions],
  );

  useEffect(() => {
    if (!leaderboardData || availableViewScopes.length === 0) {
      return;
    }

    if (!availableViewScopes.some(({ value }) => value === viewScope)) {
      setViewScope(getDefaultViewScope(leaderboardData, orgType));
    }
  }, [leaderboardData, availableViewScopes, viewScope, orgType]);

  useEffect(() => {
    if (!leaderboardData || groupScopeOptions.length === 0) {
      return;
    }

    if (!isGroupScopeEnabled(groupScopeOptions, groupScope)) {
      setGroupScope(getDefaultGroupScope(groupScopeOptions));
    }
  }, [leaderboardData, groupScopeOptions, groupScope]);

  const currentEmployeeId =
    searchParams.get('employeeId') ??
    profileData?.EmployeeId ??
    userProfile?.employeeId;
  const currentUserTeam = leaderboardData?.user_team;
  const currentUserRegion = leaderboardData?.user_region;
  const isRepView = viewScope === 'rep';
  const isRepOrSquadView = isRepView || viewScope === 'squad';
  const isMetricModeDisabled =
    isChainUser || isRepOrSquadView || isPacingUnavailable(rangeKey);

  const handleRangeKeyChange = (value: LeaderboardRangeKey) => {
    if (value === 'custom') {
      if (rangeKey !== 'custom') {
        setRangeBeforeCustom(rangeKey);
      }
      setRangeKey('custom');
      setMetricMode('actual');
      setIsCustomRangeModalOpen(true);
      return;
    }
    setRangeKey(value);
    if (isPacingUnavailable(value)) {
      setMetricMode('actual');
    }
  };

  const handleCustomRangeCancel = () => {
    setRangeKey(DEFAULT_RANGE_KEY);
    setIsCustomRangeModalOpen(false);
  };

  const handleCustomRangeConfirm = (range: LeaderboardDateRange) => {
    setCustomRange(range);
    setRangeKey('custom');
    setIsCustomRangeModalOpen(false);
  };

  const handleViewScopeChange = (value: ViewScope) => {
    setViewScope(value);

    const isRepOrSquadTarget = value === 'rep' || value === 'squad';

    if (isRepOrSquadTarget) {
      setMetricMode('actual');
    }

    if (value !== 'rep') {
      setTitleSelection('all');
    }

    if (!isRepOrSquadTarget) {
      setGroupScope(
        getDefaultGroupScope(getGroupScopeOptions(leaderboardData, orgType)),
      );
    }

    if (sortColumn === 'status') {
      setSortColumn(DEFAULT_SORT_COLUMN);
      setSortDirection('desc');
    }
  };

  const handleSortColumn = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortColumn(column);
    setSortDirection('desc');
  };

  const scopedEntries = useMemo(() => {
    if (!leaderboardData) {
      return [];
    }

    return resolveLeaderboardEntries(leaderboardData, viewScope, groupScope);
  }, [leaderboardData, viewScope, groupScope]);

  const uniqueTitles = useMemo(
    () => getUniqueTitles(scopedEntries),
    [scopedEntries],
  );

  useEffect(() => {
    setTitleSelection((current) =>
      reconcileTitleSelection(current, uniqueTitles),
    );
  }, [uniqueTitles]);

  const titleOptions = useMemo(() => {
    const effectiveSelection = reconcileTitleSelection(
      titleSelection,
      uniqueTitles,
    );
    return uniqueTitles.map((title) => ({
      id: title,
      text: title,
      checked: effectiveSelection === 'all' || effectiveSelection.has(title),
    }));
  }, [titleSelection, uniqueTitles]);

  const handleTitleChange = (
    updatedValues: { id: string; checked: boolean }[],
  ) => {
    setTitleSelection(
      titleSelectionFromCheckedIds(
        updatedValues
          .filter((option) => option.checked)
          .map((option) => option.id),
        uniqueTitles,
      ),
    );
  };

  const sortedEntries = useMemo(() => {
    const filteredEntries =
      isRepView && uniqueTitles.length > 0
        ? filterEntriesByTitle(
            scopedEntries,
            reconcileTitleSelection(titleSelection, uniqueTitles),
          )
        : scopedEntries;

    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

    return [...filteredEntries].sort((a, b) => {
      const aValue = getSortValue(
        a,
        sortColumn,
        metricMode,
        dataRangeKey,
        isBlockUser,
        viewScope,
      );
      const bValue = getSortValue(
        b,
        sortColumn,
        metricMode,
        dataRangeKey,
        isBlockUser,
        viewScope,
      );

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return directionMultiplier * aValue.localeCompare(bValue);
      }

      return directionMultiplier * ((aValue as number) - (bValue as number));
    });
  }, [
    scopedEntries,
    uniqueTitles,
    titleSelection,
    isRepView,
    isBlockUser,
    sortDirection,
    sortColumn,
    metricMode,
    dataRangeKey,
    viewScope,
  ]);

  const metricKeys = useMemo(() => {
    if (isChainUser) {
      return CHAIN_METRIC_KEYS;
    }
    if (isRepOrSquadView && isBlockUser) {
      return BLOCK_REP_METRIC_KEYS;
    }
    if (isRepOrSquadView) {
      return REP_METRIC_KEYS;
    }
    return AGGREGATED_METRIC_KEYS;
  }, [isBlockUser, isChainUser, isRepOrSquadView]);

  useEffect(() => {
    if (isChainUser && metricMode !== 'actual') {
      setMetricMode('actual');
    }
  }, [isChainUser, metricMode]);

  useEffect(() => {
    const isMetricSortColumn = sortColumn !== 'name' && sortColumn !== 'status';
    if (
      isMetricSortColumn &&
      !metricKeys.includes(sortColumn as (typeof metricKeys)[number])
    ) {
      setSortColumn(isChainUser ? 'IC' : POWER_SCORE_METRIC_KEY);
      setSortDirection('desc');
    }
  }, [isChainUser, metricKeys, sortColumn]);

  const gridTemplateColumns = getTableGridTemplateColumns({
    isRepOrSquadView,
    isBlockUser,
    isChainUser,
    isMobile,
    hasStatusColumn: isRepView,
  });

  const renderTableHeader = () => (
    <TableHeader
      data-testid="sales-ex-dashboard--leaderboard-widget--table-header"
      $gridTemplateColumns={gridTemplateColumns}
      $isMobile={isMobile}>
      <NameHeaderCell
        $isRepView={isRepView}
        $isMobile={isMobile}
        $showScrollShadow={isHorizontallyScrolled}>
        <SortableHeaderButton
          type="button"
          onClick={() => handleSortColumn('name')}
          aria-sort={getAriaSortValue(sortColumn === 'name', sortDirection)}>
          <HeaderLabel>
            <Typography
              size="small"
              fontWeight={sortColumn === 'name' ? 'medium' : undefined}
              color={
                sortColumn === 'name'
                  ? tokens.BRAND_HEROBLUE
                  : INACTIVE_HEADER_COLOR
              }>
              {(() => {
                switch (viewScope) {
                  case 'rep':
                    return 'Rep';
                  case 'squad':
                    return 'Squad';
                  case 'office':
                  case 'team':
                    return 'Team';
                  case 'metro_market':
                    return 'Metro Market';
                  case 'region':
                    return 'Region';
                  case 'vp':
                    return 'VP';
                  default: {
                    const exhaustiveCheck: never = viewScope;
                    return exhaustiveCheck;
                  }
                }
              })()}
            </Typography>
            {sortColumn === 'name' && (
              <HeaderChevron $active $asc={sortDirection === 'asc'}>
                <ChevronDownIcon />
              </HeaderChevron>
            )}
          </HeaderLabel>
        </SortableHeaderButton>
        <ActiveUnderline $active={sortColumn === 'name'} />
      </NameHeaderCell>
      {metricKeys.map((key) => {
        const isActive = sortColumn === key;
        const isPowerScore = key === POWER_SCORE_METRIC_KEY;
        const metricLabel = METRIC_LABELS[key];
        const metricInfoBody =
          key in METRIC_INFO_BODIES
            ? METRIC_INFO_BODIES[key as keyof typeof METRIC_INFO_BODIES]
            : undefined;
        const metricHeaderButton = (
          <SortableHeaderButton
            type="button"
            onClick={() => handleSortColumn(key)}
            aria-sort={getAriaSortValue(isActive, sortDirection)}>
            <HeaderLabel>
              <Typography
                size="small"
                fontWeight={isActive ? 'medium' : undefined}
                color={
                  isActive ? tokens.BRAND_HEROBLUE : INACTIVE_HEADER_COLOR
                }>
                {metricLabel}
              </Typography>
              {isActive && (
                <HeaderChevron $active $asc={sortDirection === 'asc'}>
                  <ChevronDownIcon />
                </HeaderChevron>
              )}
            </HeaderLabel>
          </SortableHeaderButton>
        );

        return (
          <MetricHeaderCell key={key}>
            {isPowerScore ? (
              <HeaderCellContent>
                {metricHeaderButton}
                <InfoIconPopover
                  content={<PowerScoreLegend />}
                  contentAriaLabel="Power Score calculation"
                  triggerAriaLabel="How Power Score is calculated"
                />
              </HeaderCellContent>
            ) : metricInfoBody ? (
              <HeaderCellContent>
                {metricHeaderButton}
                <InfoIconPopover
                  content={
                    <Typography
                      variant="span"
                      size="small"
                      color={ON_INFO_SURFACE}>
                      {metricInfoBody}
                    </Typography>
                  }
                  contentAriaLabel={`${metricLabel} metric`}
                  triggerAriaLabel={`What ${metricLabel} means`}
                />
              </HeaderCellContent>
            ) : (
              metricHeaderButton
            )}
            <ActiveUnderline $active={isActive} />
          </MetricHeaderCell>
        );
      })}
      {isRepView && (
        <StatusHeaderCell>
          <HeaderCellContent>
            <SortableHeaderButton
              type="button"
              onClick={() => handleSortColumn('status')}
              aria-sort={getAriaSortValue(
                sortColumn === 'status',
                sortDirection,
              )}>
              <HeaderLabel>
                <Typography
                  size="small"
                  fontWeight={sortColumn === 'status' ? 'medium' : undefined}
                  color={
                    sortColumn === 'status'
                      ? tokens.BRAND_HEROBLUE
                      : INACTIVE_HEADER_COLOR
                  }>
                  Status
                </Typography>
                {sortColumn === 'status' && (
                  <HeaderChevron $active $asc={sortDirection === 'asc'}>
                    <ChevronDownIcon />
                  </HeaderChevron>
                )}
              </HeaderLabel>
            </SortableHeaderButton>
            <InfoIconPopover
              content={
                <StatusLegend orgType={orgType} sellsSunrun={sellsSunrun} />
              }
              contentAriaLabel={'RunX status levels'}
              triggerAriaLabel={'How RunX status levels work'}
            />
          </HeaderCellContent>
          <ActiveUnderline $active={sortColumn === 'status'} />
        </StatusHeaderCell>
      )}
    </TableHeader>
  );

  const renderTableRows = () => (
    <Virtuoso
      customScrollParent={scrollParent ?? undefined}
      data={sortedEntries}
      defaultItemHeight={44}
      components={{
        List: VirtuosoList,
        Item: VirtuosoItem,
      }}
      itemContent={(index, entry) => (
        <LeaderboardRow
          rank={index + 1}
          entry={entry}
          metricKeys={metricKeys}
          metricMode={metricMode}
          rangeKey={dataRangeKey}
          isRepView={isRepView}
          isRepOrSquadView={isRepOrSquadView}
          viewScope={viewScope}
          isBlockUser={isBlockUser}
          isChainUser={isChainUser}
          isMobile={isMobile}
          isEven={index % 2 === 0}
          orgType={orgType}
          isHorizontallyScrolled={isHorizontallyScrolled}
          quartile={
            metricMode === 'actual' &&
            sortColumn !== 'name' &&
            (isRepOrSquadView || viewScope === 'office')
              ? getLeaderboardQuartile(
                  index,
                  sortedEntries.length,
                  sortDirection,
                )
              : null
          }
          isHighlighted={isCurrentUserRow(
            entry,
            viewScope,
            currentUserTeam,
            currentEmployeeId,
            currentUserRegion,
          )}
        />
      )}
    />
  );

  const renderTableContent = () => {
    if (isLeaderboardDataLoading) {
      return (
        <LoadingContainer>
          {[1, 2, 3, 4, 5].map((i) => (
            <StyledSkeleton key={i} />
          ))}
        </LoadingContainer>
      );
    }

    if (!leaderboardData) {
      return (
        <EmptyState>
          <Typography size="small">
            {leaderboardDataError
              ? 'Unable to load leaderboard data.'
              : 'Leaderboard data is not available.'}
          </Typography>
        </EmptyState>
      );
    }

    if (sortedEntries.length === 0) {
      return (
        <EmptyState>
          <Typography size="small">No leaderboard entries found.</Typography>
        </EmptyState>
      );
    }

    return (
      <TableScrollContainer ref={setScrollParent} onScroll={handleTableScroll}>
        <TableInner>
          {renderTableHeader()}
          {renderTableRows()}
        </TableInner>
      </TableScrollContainer>
    );
  };

  const renderTitleFilter = () =>
    isRepView && uniqueTitles.length > 0 ? (
      <ControlMultiSelect
        testId="sales-ex-dashboard--leaderboard-widget--title-filter"
        label=""
        aria-label="Title"
        triggerLabel="Title"
        selectType="button"
        buttonProps={{
          color: 'tertiary',
          size: 'sm',
        }}
        allOptionText="All"
        options={titleOptions}
        onChange={handleTitleChange}
        menuMinWidth={240}
        menuMaxHeight="available"
      />
    ) : null;

  const renderVipSelect = () =>
    isVip
      ? renderControlSelect(
          VIP_VIEW_OPTIONS,
          vipView,
          setVipView,
          false,
          true,
          '120px',
        )
      : null;

  const renderControls = () => {
    if (isMobile) {
      return (
        <MobileControlsRow>
          {renderVipSelect()}
          {renderControlSelect(
            availableViewScopes,
            viewScope,
            handleViewScopeChange,
            availableViewScopes.length <= 1,
            true,
            '50px',
          )}
          {renderTitleFilter()}
          {!isChainUser &&
            renderControlSelect(
              METRIC_MODE_OPTIONS,
              metricMode,
              setMetricMode,
              isMetricModeDisabled,
              true,
            )}
          {renderControlSelect(
            groupScopeOptions,
            groupScope,
            setGroupScope,
            !isRepOrSquadView || isGroupScopeSelectionLocked,
            true,
          )}
          {renderControlSelect(
            TIMEFRAME_OPTIONS,
            customRangeLabel ? '' : rangeKey,
            handleRangeKeyChange,
            false,
            true,
            '70px',
            customRangeLabel,
            'right',
          )}
        </MobileControlsRow>
      );
    }

    return (
      <ControlsRow>
        <ControlsGroup>
          {availableViewScopes.length > 1 && (
            <PillSwitchButtons
              testId="sales-ex-dashboard--leaderboard-widget--view-scope-toggle"
              aria-label="Leaderboard view scope"
              options={availableViewScopes}
              value={viewScope}
              onChange={handleViewScopeChange}
            />
          )}
          {renderTitleFilter()}
          {!isChainUser &&
            renderControlSelect(
              METRIC_MODE_OPTIONS,
              metricMode,
              setMetricMode,
              isMetricModeDisabled,
            )}
        </ControlsGroup>
        <ControlsGroup>
          {renderControlSelect(
            groupScopeOptions,
            groupScope,
            setGroupScope,
            !isRepOrSquadView || isGroupScopeSelectionLocked,
          )}
          {renderControlSelect(
            TIMEFRAME_OPTIONS,
            customRangeLabel ? '' : rangeKey,
            handleRangeKeyChange,
            false,
            true,
            undefined,
            customRangeLabel,
            'right',
          )}
        </ControlsGroup>
      </ControlsRow>
    );
  };

  return (
    <>
      <PerformanceWidgetSelectPortalStyles />
      <DashboardWidgetContainer
        fullWidth={isMobile}
        header={isMobile ? '' : 'Leaderboard'}
        headerContent={isMobile ? undefined : renderVipSelect()}>
        <Content>
          {renderControls()}
          <TableArea>{renderTableContent()}</TableArea>
        </Content>
      </DashboardWidgetContainer>
      {isCustomRangeModalOpen && (
        <LeaderboardDateRangeModal
          initialRange={customRange}
          onCancel={handleCustomRangeCancel}
          onConfirm={handleCustomRangeConfirm}
        />
      )}
    </>
  );
};

type LeaderboardRowProps = {
  rank: number;
  entry: LeaderboardEntry;
  metricKeys: Array<AggregatedMetricKey | RepMetricKey>;
  metricMode: MetricMode;
  rangeKey: LeaderboardRangeKey;
  isRepView: boolean;
  isRepOrSquadView: boolean;
  isBlockUser: boolean;
  isChainUser: boolean;
  isMobile: boolean;
  isEven: boolean;
  isHighlighted: boolean;
  isHorizontallyScrolled: boolean;
  orgType: NewDashboardOrgType;
  viewScope: ViewScope;
  quartile: LeaderboardQuartile | null;
};

const LeaderboardRow = ({
  rank,
  entry,
  metricKeys,
  metricMode,
  rangeKey,
  isRepView,
  isRepOrSquadView,
  isBlockUser,
  isChainUser,
  isMobile,
  isEven,
  isHighlighted,
  isHorizontallyScrolled,
  orgType,
  viewScope,
  quartile,
}: LeaderboardRowProps) => {
  const statusRank =
    isRepView && isRepEntry(entry) ? getRankFromStatusName(entry.status) : 0;

  return (
    <TableRow
      $isRepView={isRepView}
      $isEven={isEven}
      $isHighlighted={isHighlighted}
      $quartile={quartile}
      $gridTemplateColumns={getTableGridTemplateColumns({
        isRepOrSquadView,
        isBlockUser,
        isChainUser,
        isMobile,
        hasStatusColumn: isRepView,
      })}>
      <NameCell
        $isMobile={isMobile}
        $isEven={isEven}
        $quartile={quartile}
        $showScrollShadow={isHorizontallyScrolled}>
        <NameText>
          <RankText>{formatRank(rank)}</RankText>
          <LeaderboardEntityName
            name={formatLeaderboardTeamName(
              entry.name,
              isBlockUser,
              viewScope === 'office',
            )}
            isMobile={isMobile}
          />
        </NameText>
      </NameCell>
      {metricKeys.map((key) => {
        const tier =
          !isRepOrSquadView && metricMode === 'pacing'
            ? getMetricPaceTier(entry, key as AggregatedMetricKey, rangeKey)
            : undefined;
        const textColor = tier ? TIER_COLORS[tier] : tokens.BRAND_HEROBLUE;

        return (
          <MetricValueCell key={key} $isEven={isEven} $quartile={quartile}>
            <Typography
              size="small"
              fontWeight={tier ? 'bold' : undefined}
              color={textColor}>
              {formatMetricValue(
                getMetricValue(entry, key, metricMode, rangeKey),
                metricMode,
                key,
              )}
            </Typography>
          </MetricValueCell>
        );
      })}
      {isRepView && isRepEntry(entry) && (
        <StatusCell $isEven={isEven} $quartile={quartile}>
          {statusRank > 0 ? (
            <StatusContent>
              <Typography size="small" color={tokens.BRAND_HEROBLUE}>
                {getStatusShortLabel(statusRank, orgType)}
              </Typography>
              <StatusIconSlot>
                {getIconForRankValue({
                  rank: statusRank,
                  sizePx: 24,
                  opacity: 1,
                  orgType,
                })}
              </StatusIconSlot>
            </StatusContent>
          ) : (
            <Typography size="small" color={INACTIVE_HEADER_COLOR}>
              -
            </Typography>
          )}
        </StatusCell>
      )}
    </TableRow>
  );
};

type LeaderboardEntityNameProps = {
  name: string;
  isMobile: boolean;
};

const LeaderboardEntityName = ({
  name,
  isMobile,
}: LeaderboardEntityNameProps) => {
  const maxChars = isMobile ? 15 : 22;
  const shouldShowTooltip = name.length > maxChars;

  if (!shouldShowTooltip) {
    return <EntityName>{name}</EntityName>;
  }

  return (
    <LeaderboardNameTooltip content={name} direction="top-left">
      <EntityName>{name}</EntityName>
    </LeaderboardNameTooltip>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const MobileControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 12px;
  overflow-x: auto;
  flex-shrink: 0;
  scrollbar-width: thin;
  scrollbar-color: ${tokens.TINTS_OFF_WHITE_20} transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${tokens.TINTS_OFF_WHITE_20};
    border-radius: 3px;
  }

  > * {
    flex-shrink: 0;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0 12px 12px;
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ControlSelect = styled(PerformanceWidgetSelect)<{ $minWidth?: string }>`
  min-width: unset;
  max-width: unset;
  ${({ $minWidth }) =>
    $minWidth &&
    `
    .sr-select-container > button > span {
      min-width: ${$minWidth};
    }
  `}

  .sr-select-container ul {
    min-width: 160px;
  }
`;

const ControlMultiSelect = styled(PerformanceWidgetMultiSelect)`
  min-width: unset;
  max-width: unset;
`;

const TableArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 200px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: rgb(235, 233, 228);
  overflow: hidden;
`;

const TableScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: ${tokens.TINTS_OFF_WHITE_20} transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${tokens.TINTS_OFF_WHITE_20};
    border-radius: 3px;
  }
`;

const TableInner = styled.div`
  width: max-content;
  min-width: 100%;
`;

const VirtuosoList = styled.div`
  width: max-content;
  min-width: 100%;
`;

const VirtuosoItem = styled.div`
  width: max-content;
  min-width: 100%;
`;

const TableHeader = styled.div<{
  $gridTemplateColumns: string;
  $isMobile?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  column-gap: 4px;
  align-items: end;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: rgb(235, 233, 228);
  padding: 0 8px 0 0;
  flex-shrink: 0;
`;

const NameHeaderCell = styled.div<{
  $isRepView: boolean;
  $isMobile?: boolean;
  $showScrollShadow?: boolean;
}>`
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  min-width: 0;
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: rgb(235, 233, 228);

  ${({ $showScrollShadow }) =>
    $showScrollShadow && `box-shadow: 4px 0 8px -2px rgba(31, 38, 71, 0.12);`}
`;

const StatusHeaderCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  min-width: 0;
  margin-top: 10px;
`;

const MetricHeaderCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 10px;
`;

const ActiveUnderline = styled.div<{ $active: boolean }>`
  width: 100%;
  height: 3px;
  border-radius: 24px 24px 0 0;
  background-color: ${({ $active }) =>
    $active ? tokens.BRAND_HEROBLUE : 'transparent'};
`;

const SortableHeaderButton = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${tokens.BRAND_HEROBLUE};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const HeaderLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;

  > p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const HeaderChevron = styled.span<{ $active?: boolean; $asc?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ $active }) =>
    $active ? tokens.BRAND_HEROBLUE : INACTIVE_HEADER_COLOR};
  transform: ${({ $asc }) => ($asc ? 'rotate(180deg)' : 'none')};
`;

const TableRow = styled.div<{
  $isRepView: boolean;
  $isEven: boolean;
  $isHighlighted: boolean;
  $quartile: LeaderboardQuartile | null;
  $gridTemplateColumns: string;
}>`
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  column-gap: 4px;
  align-items: stretch;
  box-sizing: border-box;
  padding: 0 8px 0 0;
  background: ${({ $isEven, $quartile }) =>
    getLeaderboardRowBackgroundColor({
      isEven: $isEven,
      quartile: $quartile,
    })};
  border: 2px solid
    ${({ $isHighlighted, $quartile }) =>
      $isHighlighted
        ? getLeaderboardRowHighlightBorderColor($quartile)
        : 'transparent'};
`;

const RankText = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.28px;
  color: ${tokens.BRAND_HEROBLUE};
`;

const EntityName = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: normal;
  color: ${tokens.BRAND_HEROBLUE};
`;

const LeaderboardNameTooltip = styled(Tooltip)`
  flex: 1;
  min-width: 0;

  .sr-tooltip-children {
    min-width: 0;
    overflow: hidden;
  }

  [role='tooltip'] {
    padding: 11px 8px;
    border-radius: 6px;
    max-width: none;
    margin: 4px 0;
    white-space: nowrap;

    > p {
      font-size: 12px;
      line-height: 1.4;
      letter-spacing: 0;
      white-space: nowrap;
    }
  }
`;

const NameText = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
`;

const NameCell = styled.div<{
  $isMobile?: boolean;
  $isEven?: boolean;
  $quartile?: LeaderboardQuartile | null;
  $showScrollShadow?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  min-height: 44px;
  min-width: 0;
  padding: 12px 0 12px 8px;
  position: sticky;
  left: 0;
  background: ${({ $isEven, $quartile }) =>
    getLeaderboardRowBackgroundColor({
      isEven: $isEven ?? false,
      quartile: $quartile ?? null,
    })};

  ${({ $showScrollShadow }) =>
    $showScrollShadow && `box-shadow: 4px 0 8px -2px rgba(31, 38, 71, 0.12);`}
`;

const MetricValueCell = styled.div<{
  $isEven: boolean;
  $quartile: LeaderboardQuartile | null;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  background: ${({ $isEven, $quartile }) =>
    getLeaderboardRowBackgroundColor({
      isEven: $isEven,
      quartile: $quartile,
    })};

  > p {
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StatusCell = styled.div<{
  $isEven: boolean;
  $quartile: LeaderboardQuartile | null;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 0;
  background: ${({ $isEven, $quartile }) =>
    getLeaderboardRowBackgroundColor({
      isEven: $isEven,
      quartile: $quartile,
    })};
`;

const StatusContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  min-width: 42px;
`;

const StatusIconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  & img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const HeaderCellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  flex: 1;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 100%;
  min-height: 36px;
  border-radius: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 24px;
`;

export {
  LeaderboardWidget,
  getMetricValue,
  getMetricPaceTier,
  getTableGridTemplateColumns,
  TIMEFRAME_OPTIONS as LEADERBOARD_TIMEFRAME_OPTIONS,
};
